/**
 * Capacitor App Initialization
 * This file initializes Capacitor plugins and handles app lifecycle events
 * Import this in your main App.js to enable native features
 */

import { isNative, getPlatform } from './services/platform/platformDetector';
import pushNotificationService from './services/pushNotificationService';

// App lifecycle management
let appListenerCleanup = null;

/**
 * Initialize Capacitor app features
 * Should be called once when the app starts
 */
export const initializeCapacitor = async () => {
  if (!isNative()) {
    console.log('[Capacitor] Running in web mode');
    return;
  }

  console.log(`[Capacitor] Running on ${getPlatform()}`);

  try {
    // Initialize Status Bar
    await initStatusBar();
    
    // Initialize Splash Screen
    await initSplashScreen();
    
    // Setup App Lifecycle Listeners
    await setupAppListeners();
    
    // Setup keyboard handling
    setupKeyboardHandling();
    
    // Initialize Push Notifications
    await initPushNotifications();
    
    console.log('[Capacitor] Initialization complete');
  } catch (error) {
    console.error('[Capacitor] Initialization error:', error);
  }
};

/**
 * Initialize Push Notifications
 */
const initPushNotifications = async () => {
  try {
    const result = await pushNotificationService.initialize();
    if (result.success) {
      console.log('[Capacitor] Push notifications initialized');
      
      // Listen for notification events
      pushNotificationService.on('notificationReceived', (notification) => {
        console.log('[Capacitor] Notification received:', notification);
        // Dispatch custom event for components to handle
        window.dispatchEvent(new CustomEvent('pushNotification', { detail: notification }));
      });
      
      pushNotificationService.on('notificationTapped', (action) => {
        console.log('[Capacitor] Notification tapped:', action);
        // Handle navigation based on notification data
        window.dispatchEvent(new CustomEvent('pushNotificationTapped', { detail: action }));
      });
    } else {
      console.warn('[Capacitor] Push notifications not available:', result.error);
    }
  } catch (e) {
    console.warn('[Capacitor] Push notifications error:', e.message);
  }
};

/**
 * Initialize Status Bar styling
 */
const initStatusBar = async () => {
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    
    // Set status bar style based on platform
    if (getPlatform() === 'ios') {
      await StatusBar.setStyle({ style: Style.Dark });
    } else {
      await StatusBar.setBackgroundColor({ color: '#1e3a5f' });
      await StatusBar.setStyle({ style: Style.Dark });
    }
  } catch (e) {
    console.warn('[Capacitor] StatusBar not available:', e.message);
  }
};

/**
 * Initialize and hide Splash Screen
 */
const initSplashScreen = async () => {
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    
    // Hide splash screen after a brief delay to ensure app is rendered
    setTimeout(async () => {
      await SplashScreen.hide({ fadeOutDuration: 300 });
    }, 500);
  } catch (e) {
    console.warn('[Capacitor] SplashScreen not available:', e.message);
  }
};

/**
 * Setup App lifecycle listeners
 */
const setupAppListeners = async () => {
  try {
    const { App } = await import('@capacitor/app');
    
    // Handle app state changes (foreground/background)
    const stateListener = await App.addListener('appStateChange', ({ isActive }) => {
      console.log(`[Capacitor] App state: ${isActive ? 'active' : 'background'}`);
      
      // Dispatch custom event for components to listen to
      window.dispatchEvent(new CustomEvent('appStateChange', { detail: { isActive } }));
    });
    
    // Handle back button (Android)
    const backListener = await App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        // Optionally show exit confirmation or minimize app
        App.minimizeApp();
      }
    });
    
    // Handle deep links
    const urlListener = await App.addListener('appUrlOpen', ({ url }) => {
      console.log('[Capacitor] Deep link:', url);
      // Handle deep links here - navigate to appropriate route
      window.dispatchEvent(new CustomEvent('deepLink', { detail: { url } }));
    });
    
    // Store cleanup functions
    appListenerCleanup = () => {
      stateListener.remove();
      backListener.remove();
      urlListener.remove();
    };
  } catch (e) {
    console.warn('[Capacitor] App plugin not available:', e.message);
  }
};

/**
 * Setup keyboard handling for better mobile UX
 */
const setupKeyboardHandling = () => {
  if (!isNative()) return;
  
  // Add class to body when keyboard is visible
  const handleResize = () => {
    const isKeyboardVisible = window.innerHeight < window.screen.height * 0.75;
    document.body.classList.toggle('keyboard-visible', isKeyboardVisible);
  };
  
  window.addEventListener('resize', handleResize);
  
  // Handle iOS keyboard viewport issues
  if (getPlatform() === 'ios') {
    document.addEventListener('focusin', (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Scroll input into view after keyboard opens
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    });
  }
};

/**
 * Cleanup Capacitor listeners
 * Call this when app unmounts
 */
export const cleanupCapacitor = () => {
  if (appListenerCleanup) {
    appListenerCleanup();
    appListenerCleanup = null;
  }
};

/**
 * Handle safe area insets for notched devices
 * Returns CSS-compatible inset values
 */
export const getSafeAreaInsets = () => {
  if (typeof CSS !== 'undefined' && CSS.supports('padding-top: env(safe-area-inset-top)')) {
    return {
      top: 'env(safe-area-inset-top)',
      right: 'env(safe-area-inset-right)',
      bottom: 'env(safe-area-inset-bottom)',
      left: 'env(safe-area-inset-left)'
    };
  }
  return { top: '0px', right: '0px', bottom: '0px', left: '0px' };
};

export default initializeCapacitor;
